/**
 * Inbox filtering for Broadcast messages on the employee Home and Broadcast module.
 * Workplace-wide and organization-wide messages use receiverGroup, not receiverUser.
 * Workplace scoping (including Entire Organization visibility) is handled by the inbox API.
 */

export function getBroadcastReceiverUserId(message: unknown): number | null {
	if (!message || typeof message !== 'object') return null;

	const receiverUser = (message as Record<string, unknown>).receiverUser;
	if (!receiverUser) return null;

	if (typeof receiverUser === 'object') {
		const user = receiverUser as Record<string, unknown>;
		if (user.id != null) return Number(user.id);
		if (typeof user['@id'] === 'string') {
			return parseInt(user['@id'].split('/').pop() || '0', 10) || null;
		}
	}

	if (typeof receiverUser === 'string') {
		return parseInt(receiverUser.split('/').pop() || '0', 10) || null;
	}

	return null;
}

export function isOrganizationBroadcast(message: unknown): boolean {
	if (!message || typeof message !== 'object') return false;

	const m = message as Record<string, unknown>;
	const receiverGroup = m.receiverGroup as Record<string, unknown> | null | undefined;

	return (
		receiverGroup?.organization_broadcast === true ||
		receiverGroup?.isEntireOrganization === true ||
		m.organization_broadcast === true ||
		Boolean(
			receiverGroup?.groupName &&
				String(receiverGroup.groupName).toLowerCase().includes('entire organization')
		)
	);
}

export function isWorkplaceBroadcast(message: unknown): boolean {
	if (!message || typeof message !== 'object') return false;

	const m = message as Record<string, unknown>;
	const receiverGroup = m.receiverGroup as Record<string, unknown> | null | undefined;

	if (!receiverGroup) return false;

	const groupName = String(receiverGroup.groupName || '')
		.toLowerCase()
		.trim();

	return (
		((receiverGroup.workplace_broadcast === true || receiverGroup.isEntireWorkplace === true) &&
			!isOrganizationBroadcast(message)) ||
		(groupName.includes('entire workplace') && !isOrganizationBroadcast(message))
	);
}

/**
 * Custom broadcast group (built from users or security roles). The inbox API only returns
 * these to group members, so no client-side membership check is needed.
 */
export function isCustomGroupBroadcast(message: unknown): boolean {
	if (!message || typeof message !== 'object') return false;

	const receiverGroup = (message as Record<string, unknown>).receiverGroup;
	if (!receiverGroup) return false;

	return !isOrganizationBroadcast(message) && !isWorkplaceBroadcast(message);
}

export function isDirectUserBroadcast(message: unknown, currentUserId: number | null): boolean {
	if (currentUserId == null) return false;
	return getBroadcastReceiverUserId(message) === currentUserId;
}

function mergeUniqueMessages<T>(items: T[]): T[] {
	const seen = new Set<unknown>();
	return items.filter((item) => {
		const key =
			item && typeof item === 'object' && 'id' in item
				? (item as { id: unknown }).id
				: JSON.stringify(item);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

export interface BroadcastInboxFilterOptions {
	currentUserId: number | null;
	canViewBroadcastMy: boolean;
	canViewBroadcastAll: boolean;
	canViewBroadcastOrganization: boolean;
}

/**
 * Returns inbox messages visible to the current user based on Broadcast permissions.
 * Matches the Broadcast module inbox rules, including workplace/org-wide messages.
 */
export function filterBroadcastInboxForUser(
	inbox: unknown[],
	options: BroadcastInboxFilterOptions
): unknown[] {
	const messages = Array.isArray(inbox) ? inbox : [];

	if (options.canViewBroadcastAll) {
		return options.canViewBroadcastOrganization
			? messages
			: messages.filter((message) => !isOrganizationBroadcast(message));
	}

	const buckets: unknown[] = [];

	if (options.canViewBroadcastMy) {
		buckets.push(
			...messages.filter((message) => isDirectUserBroadcast(message, options.currentUserId))
		);
		buckets.push(...messages.filter(isWorkplaceBroadcast));
		buckets.push(...messages.filter(isCustomGroupBroadcast));
	}

	if (options.canViewBroadcastOrganization) {
		buckets.push(...messages.filter(isOrganizationBroadcast));
	}

	return mergeUniqueMessages(buckets);
}
